import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { CountrySelect } from '@/components/ui/country-select'
import { ProfileCardShell } from '@/components/ProfileCardShell'
import { apiErrorMessage } from '@/lib/api-error'
import { getCountriesApi, type Country, type UserTaxInfo } from '@/lib/api'
import {
  AlertTriangleIcon,
  Loader2,
  PencilIcon,
  ReceiptTextIcon,
  RefreshCwIcon,
  SaveIcon,
  XIcon,
} from 'lucide-react'

/** The writable tax-information payload — identical shape for the
 * self-service (PUT /users/tax-info) and employer-managed
 * (PUT /organizations/users/:userId/tax-info) endpoints. */
export interface TaxInfoUpdatePayload {
  tax_residency_country_id: number
  tax_identifier?: string | null
  tax_identifier_type?: string | null
  foreign_tax_id?: string | null
  w9_completed?: boolean
  w8ben_completed?: boolean
  is_us_person?: boolean
  withholding_rate?: number | null
}

/** Copy overrides so the two consumers (self-service vs employer-managed)
 * keep their distinct wording without forking the component. */
export interface TaxInfoCardCopy {
  loadErrorMessage?: string
  selectCountryMessage?: string
  successMessage?: string
  emptyState?: ReactNode
  viewNote?: ReactNode
  taxIdPlaceholder?: string
  taxIdHelper?: string
}

interface TaxInfoFormCardProps {
  /** Fetch the current tax info — own (self-service) or a specific employee's. */
  fetchTaxInfo: () => Promise<{ tax_info: UserTaxInfo | null }>
  /** Persist an edited payload. */
  saveTaxInfo: (payload: TaxInfoUpdatePayload) => Promise<unknown>
  /** Reload the card whenever this value changes (e.g. the employee userId). */
  reloadKey?: unknown
  /** Prefix for input ids so several cards on one page never collide. */
  idPrefix?: string
  /** Copy overrides; the self-service wording is the default. */
  copy?: TaxInfoCardCopy
}

/**
 * Shared tax-information card (DESIGN_TaxFilingProvision.md §3.2/§3.3).
 * Shows the masked tax profile of a user and lets an authorized editor
 * (the user themselves, or their employer on the compliance path) enter or
 * correct tax residency + identifiers. TaxInfoCard (self-service) and
 * EmployeeTaxInfoCard (employer-managed) are thin wrappers that supply only
 * the data access + wording.
 */
export function TaxInfoFormCard({
  fetchTaxInfo,
  saveTaxInfo,
  reloadKey,
  idPrefix = 'tax',
  copy = {},
}: TaxInfoFormCardProps) {
  const [taxInfo, setTaxInfo] = useState<UserTaxInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  const [countries, setCountries] = useState<Country[]>([])
  const [countryId, setCountryId] = useState<number | ''>('')
  const [taxIdentifier, setTaxIdentifier] = useState('')
  const [taxIdentifierType, setTaxIdentifierType] = useState('')
  const [w9Completed, setW9Completed] = useState(false)
  const [w8benCompleted, setW8benCompleted] = useState(false)
  const [isUsPerson, setIsUsPerson] = useState(false)
  const [withholdingRate, setWithholdingRate] = useState('')

  const [saving, setSaving] = useState(false)

  const loadErrorMessage = copy.loadErrorMessage ?? 'Failed to load tax information'
  const selectCountryMessage = copy.selectCountryMessage ?? 'Select your tax residency country'
  const successMessage = copy.successMessage ?? 'Tax information updated'
  const taxIdPlaceholder = copy.taxIdPlaceholder ?? 'Last digits are stored; shown masked'
  const taxIdHelper =
    copy.taxIdHelper ?? 'Enter your full identifier. Only the last 4 characters are ever displayed.'
  const emptyState =
    copy.emptyState ??
    'No tax information recorded yet. Add your tax residency and identifier so your ' +
      'organization can include you in the annual tax data package.'
  const viewNote =
    copy.viewNote ?? (
      <p className="text-xs text-muted-foreground sm:col-span-2">
        Identifiers are masked for your privacy. Your organization is responsible for the
        annual employee tax declaration.
      </p>
    )

  const refresh = async () => {
    const res = await fetchTaxInfo()
    setTaxInfo(res.tax_info)
  }

  const load = async (signal?: { cancelled: boolean }) => {
    setLoading(true)
    setLoadError(null)
    try {
      const [infoRes, countriesRes] = await Promise.all([fetchTaxInfo(), getCountriesApi()])
      if (signal?.cancelled) return
      setTaxInfo(infoRes.tax_info)
      setCountries(countriesRes.data)
    } catch (err) {
      if (signal?.cancelled) return
      setLoadError(apiErrorMessage(err, loadErrorMessage))
      setTaxInfo(null)
    } finally {
      if (!signal?.cancelled) setLoading(false)
    }
  }

  useEffect(() => {
    const signal = { cancelled: false }
    load(signal)
    return () => {
      signal.cancelled = true
    }
    // reloadKey is an identity marker (e.g. the employee userId) — the effect
    // must re-run when it changes. Mirrors the original per-card effect.
  }, [reloadKey])

  const startEdit = () => {
    setEditing(true)
    setCountryId(taxInfo?.tax_residency_country_id ?? '')
    setTaxIdentifier('')
    setTaxIdentifierType(taxInfo?.tax_identifier_type ?? '')
    setW9Completed(taxInfo?.w9_completed ?? false)
    setW8benCompleted(taxInfo?.w8ben_completed ?? false)
    setIsUsPerson(taxInfo?.is_us_person ?? false)
    setWithholdingRate(taxInfo?.withholding_rate != null ? String(taxInfo.withholding_rate) : '')
  }

  const cancelEdit = () => {
    setEditing(false)
    setSaving(false)
  }

  const handleSave = async () => {
    if (countryId === '') {
      toast.error(selectCountryMessage)
      return
    }
    setSaving(true)
    try {
      const rate = withholdingRate.trim() === '' ? null : Number(withholdingRate)
      // Only send tax_identifier when a new one was typed — an explicit null
      // tells the API to CLEAR the stored value, so saving an edit without
      // re-entering the (masked) ID must not wipe it.
      const payload: TaxInfoUpdatePayload = {
        tax_residency_country_id: countryId as number,
        tax_identifier_type: taxIdentifierType.trim() === '' ? null : taxIdentifierType.trim(),
        w9_completed: w9Completed,
        w8ben_completed: w8benCompleted,
        is_us_person: isUsPerson,
        withholding_rate: rate,
      }
      if (taxIdentifier.trim() !== '') payload.tax_identifier = taxIdentifier.trim()
      await saveTaxInfo(payload)
      setEditing(false)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update tax information'))
      setSaving(false)
      return
    }
    // Refresh outside the try so a failed reload can't misreport the save.
    toast.success(successMessage)
    try {
      await refresh()
    } catch {
      toast.error('Saved, but could not refresh the display')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading tax information…
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangleIcon className="size-4 shrink-0" />
            {loadError}
          </div>
          <Button variant="outline" size="sm" className="w-fit gap-1.5" onClick={() => load()}>
            <RefreshCwIcon className="size-3.5" /> Retry
          </Button>
        </div>
      </div>
    )
  }

  const countryName =
    countries.find((c) => c.country_id === taxInfo?.tax_residency_country_id)?.country ??
    taxInfo?.country_name ??
    '—'

  return (
    <ProfileCardShell
      icon={ReceiptTextIcon}
      title="Tax information"
      right={
        !editing && (
          <Button variant="ghost" size="sm" onClick={startEdit} className="gap-1.5">
            <PencilIcon className="size-3.5" />
            {taxInfo ? 'Edit' : 'Add'}
          </Button>
        )
      }
    >

      {!editing ? (
        taxInfo ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tax residency" value={countryName} />
            <Field
              label={taxInfo.tax_identifier_type ? `Tax ID (${taxInfo.tax_identifier_type})` : 'Tax ID'}
              value={taxInfo.tax_identifier_masked ?? 'Not recorded'}
            />
            <Field label="W-9" value={taxInfo.w9_completed ? 'Complete' : 'Not completed'} />
            <Field label="W-8BEN" value={taxInfo.w8ben_completed ? 'Complete' : 'Not completed'} />
            {taxInfo.withholding_rate != null && (
              <Field label="Withholding rate" value={`${taxInfo.withholding_rate}%`} />
            )}
            {viewNote}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{emptyState}</p>
        )
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-country`}>Tax residency country</Label>
              <CountrySelect
                id={`${idPrefix}-country`}
                value={countryId}
                onChange={setCountryId}
                countries={countries}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-id-type`}>Tax ID type</Label>
              <Input
                id={`${idPrefix}-id-type`}
                value={taxIdentifierType}
                onChange={(e) => setTaxIdentifierType(e.target.value)}
                placeholder="e.g. SSN, ITIN, RFC"
                maxLength={30}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-id`}>Tax ID number</Label>
              <Input
                id={`${idPrefix}-id`}
                value={taxIdentifier}
                onChange={(e) => setTaxIdentifier(e.target.value)}
                placeholder={taxIdPlaceholder}
                maxLength={50}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">{taxIdHelper}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}-withholding`}>Withholding rate (%)</Label>
              <Input
                id={`${idPrefix}-withholding`}
                value={withholdingRate}
                onChange={(e) => setWithholdingRate(e.target.value)}
                placeholder="Optional"
                inputMode="decimal"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <Checkbox checked={w9Completed} onCheckedChange={(v) => setW9Completed(v === true)} />
              W-9 completed (US)
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={w8benCompleted}
                onCheckedChange={(v) => setW8benCompleted(v === true)}
              />
              W-8BEN completed (foreign person)
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <Checkbox checked={isUsPerson} onCheckedChange={(v) => setIsUsPerson(v === true)} />
              US person (for FATCA reporting)
            </label>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="size-4 animate-spin" /> : <SaveIcon className="size-4" />}
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="outline" onClick={cancelEdit} disabled={saving} className="gap-1.5">
              <XIcon className="size-4" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </ProfileCardShell>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="truncate text-sm font-medium">{value}</p>
    </div>
  )
}
